/**
 * Pure description of the chord "bubble" that pops up while a chord is held.
 *
 * Opacity and scale are derived from elapsed time rather than accumulated in
 * React state. Storing them in state meant the animation loop wrote state on
 * every frame, which re-ran the effect that owned the loop, which scheduled
 * another `requestAnimationFrame` on top of the one already running: the number
 * of live loops grew for as long as a chord was held, and none of them were
 * cancelled on unmount.
 *
 * Keeping the frame a function of time leaves only the bubble's *identity*
 * (which chord, when it started, when it was let go) in state, so the state
 * changes a handful of times per chord instead of sixty times per second.
 */

export interface ChordBubble {
  chord: string;
  /** `Date.now()` when the bubble appeared. */
  startTime: number;
  /** True once the notes were released and the bubble is fading out. */
  isReleased: boolean;
  /** `Date.now()` of the release; meaningless while `isReleased` is false. */
  releaseTime: number;
}

export interface ChordBubbleFrame {
  opacity: number;
  scale: number;
}

/** How long a released bubble stays on screen before it is cleared. */
export const CHORD_BUBBLE_MIN_DISPLAY_MS = 500;
/** Fade-in ramp measured from `startTime`. */
const FADE_IN_MS = 300;
/** Fade-out ramp, ending exactly as the minimum display time runs out. */
const FADE_OUT_MS = 200;
/** Opacity floor during fade-in, so a new bubble is never invisible. */
const MIN_FADE_IN_OPACITY = 0.1;

const GROW_MS = 200;
const OVERSHOOT_END_MS = 400;
const SETTLE_END_MS = 600;
const MIN_SCALE = 0.5;
const OVERSHOOT_SCALE = 1.1;

/** A released bubble is done once it has served its minimum display time. */
export const isChordBubbleExpired = (
  bubble: ChordBubble,
  nowMs: number,
): boolean =>
  bubble.isReleased &&
  nowMs - bubble.releaseTime >= CHORD_BUBBLE_MIN_DISPLAY_MS;

const computeOpacity = (bubble: ChordBubble, nowMs: number): number => {
  const elapsed = nowMs - bubble.startTime;

  if (elapsed < FADE_IN_MS) {
    return Math.max(MIN_FADE_IN_OPACITY, elapsed / FADE_IN_MS);
  }

  // A held chord stays at full opacity for as long as it is held.
  if (!bubble.isReleased) return 1;

  const timeSinceRelease = nowMs - bubble.releaseTime;
  const fadeOutStart = Math.max(0, CHORD_BUBBLE_MIN_DISPLAY_MS - FADE_OUT_MS);
  if (timeSinceRelease <= fadeOutStart) return 1;

  const fadeOutProgress = (timeSinceRelease - fadeOutStart) / FADE_OUT_MS;
  return Math.max(0, 1 - fadeOutProgress);
};

const computeScale = (bubble: ChordBubble, nowMs: number): number => {
  const elapsed = nowMs - bubble.startTime;

  // Grow in, overshoot slightly, settle back, then hold while the chord is held.
  if (elapsed < GROW_MS) {
    return Math.max(MIN_SCALE, MIN_SCALE + (elapsed / GROW_MS) * 0.6);
  }
  if (elapsed < OVERSHOOT_END_MS) return OVERSHOOT_SCALE;
  if (elapsed < SETTLE_END_MS) {
    const settleProgress = (elapsed - OVERSHOOT_END_MS) / 200;
    return OVERSHOOT_SCALE - settleProgress * 0.1;
  }
  return 1;
};

/** How the bubble should be painted at `nowMs`. */
export const computeChordBubbleFrame = (
  bubble: ChordBubble,
  nowMs: number,
): ChordBubbleFrame => ({
  opacity: computeOpacity(bubble, nowMs),
  scale: computeScale(bubble, nowMs),
});
